import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { KPI } from "../../components/Brand";
import { currency } from "../../lib/status";

export default function AdminPlatform() {
    const [data, setData] = useState(null);
    const [audit, setAudit] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [d, a] = await Promise.all([
                    api.get("/platform/dashboard"),
                    api.get("/audit-logs"),
                ]);
                setData(d.data);
                setAudit(a.data);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
            }
        })();
    }, []);

    if (!data) {
        return <div className="text-white/60 p-8 text-center">Loading…</div>;
    }

    const k = data.kpi;

    return (
        <div className="space-y-8">
            <div>
                <div className="lx-label text-[#D4AF37] mb-2">Super Admin</div>
                <h1 className="font-serif text-4xl text-white">
                    Platform <span className="italic">overview</span>.
                </h1>
                <p className="text-white/60 text-sm mt-2">
                    Cross-company analytics and audit trail.
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI label="Companies" value={k.companies} testid="pkpi-companies" />
                <KPI label="Total Users" value={k.users_total} sub={`${k.blocked_users} blocked`} testid="pkpi-users" />
                <KPI label="Technicians" value={`${k.technicians_online}/${k.technicians}`} sub="Online / total" testid="pkpi-tech" />
                <KPI label="Customers" value={k.customers} testid="pkpi-cust" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI label="Today bookings" value={k.today_bookings} sub={`${k.total_bookings} total`} testid="pkpi-tb" />
                <KPI label="Revenue (all-time)" value={currency(k.revenue_total, "AED")} testid="pkpi-rev" />
                <KPI label="Revenue this month" value={currency(k.revenue_month, "AED")} testid="pkpi-rm" />
                <KPI label="Platform commission" value={currency(k.platform_commission, "AED")} testid="pkpi-comm" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <KPI label="Active memberships" value={k.active_members} testid="pkpi-mem" />
                <KPI label="Total referrals" value={k.total_referrals} testid="pkpi-ref" />
                <KPI label="Credited referrals" value={k.credited_referrals} testid="pkpi-refc" />
            </div>

            <div className="lx-card-dark overflow-x-auto">
                <div className="px-4 py-3 border-b border-[#2B1438]">
                    <div className="lx-label text-[#D4AF37]">By company</div>
                </div>
                <table className="w-full text-sm">
                    <thead className="text-left text-white/60 border-b border-[#2B1438]">
                        <tr>
                            <th className="p-4 lx-label">Company</th>
                            <th className="p-4 lx-label">Code</th>
                            <th className="p-4 lx-label">Country</th>
                            <th className="p-4 lx-label text-right">Bookings</th>
                            <th className="p-4 lx-label text-right">Revenue</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.by_company.map((c) => (
                            <tr
                                key={c.company_id}
                                className="border-b border-[#2B1438]"
                                data-testid={`pf-company-${c.company_id}`}
                            >
                                <td className="p-4 text-white">{c.name}</td>
                                <td className="p-4 text-white/60 font-mono text-xs">
                                    {c.code || "—"}
                                </td>
                                <td className="p-4 text-white/60">{c.country || "—"}</td>
                                <td className="p-4 text-right text-white">{c.bookings}</td>
                                <td className="p-4 text-right font-serif text-[#D4AF37]">
                                    {currency(c.revenue, c.currency || "AED")}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {data.by_company.length === 0 && (
                    <div className="p-12 text-center text-white/60">No data.</div>
                )}
            </div>

            <div className="lx-card-dark">
                <div className="px-4 py-3 border-b border-[#2B1438] flex items-center justify-between">
                    <div className="lx-label text-[#D4AF37]">Audit log (latest)</div>
                    <span className="text-xs text-white/40">{audit.length} events</span>
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {audit.slice(0, 60).map((e) => (
                        <div
                            key={e.id || e._id}
                            data-testid={`audit-row-${e.id || e._id}`}
                            className="px-4 py-2 border-b border-[#2B1438] text-xs flex items-center justify-between gap-3"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-white/80 font-mono">{e.action}</div>
                                <div className="text-white/40 truncate">
                                    {e.entity_type} · {e.entity_id}
                                </div>
                            </div>
                            <div className="text-white/40">{e.created_at?.slice(0, 19).replace("T", " ")}</div>
                        </div>
                    ))}
                    {audit.length === 0 && (
                        <div className="p-8 text-center text-white/60">No audit events yet.</div>
                    )}
                </div>
            </div>
        </div>
    );
}
